import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, ArrowUpRight, Check } from "lucide-react";
import { CHAIR_CATEGORIES, WHATSAPP_LINK } from "../../data/site";

const waFor = (chair, catLabel) =>
    `https://wa.me/917799157203?text=${encodeURIComponent(
        `Hello Zam Zam Furnitures, I want a bulk price quote for the ${chair.name} (${chair.model}) — ${catLabel}.`
    )}`;

export const ChairCollection = () => {
    const [active, setActive] = useState(CHAIR_CATEGORIES[0].id);
    const cat = CHAIR_CATEGORIES.find((c) => c.id === active);

    return (
        <section id="chair-collection" data-testid="chair-collection-section" className="bg-white py-24 lg:py-32 border-t border-line">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <p className="font-mono text-[11px] tracking-[0.35em] text-gold mb-6" data-testid="chairs-overline">
                            TASKLANCE® SEATING · ISO 9001:2015
                        </p>
                        <h2 className="font-heading font-extrabold tracking-tighter text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="chairs-heading">
                            THE ERGONOMIC CHAIR<br />COLLECTION.
                        </h2>
                        <p className="mt-6 max-w-xl text-muted text-base md:text-lg leading-relaxed" data-testid="chairs-copy">
                            Workstation, Manager, MD and Visitor chairs — straight from our factory catalog.
                            Frame colours in Black, White and Grey. 40+ models available; featured range below.
                        </p>
                    </motion.div>
                    <motion.a
                        href="/catalogs/TASKLANCE-Office-Chairs.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="group inline-flex w-fit items-center gap-2 px-7 py-4 bg-navy text-white font-medium tracking-wide hover:bg-gold transition-colors duration-300"
                        data-testid="chairs-catalog-button"
                    >
                        Download Full Chairs Catalog
                        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </motion.a>
                </div>

                <div className="flex flex-wrap gap-2 mb-10 border-b border-line pb-6" data-testid="chair-category-tabs">
                    {CHAIR_CATEGORIES.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setActive(c.id)}
                            data-testid={`chair-tab-${c.id}`}
                            className={`px-5 py-3 font-mono text-[11px] md:text-xs tracking-[0.2em] uppercase border transition-colors duration-300 ${
                                active === c.id
                                    ? "bg-navy text-white border-navy"
                                    : "bg-white text-charcoal border-line hover:border-gold hover:text-gold"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                <p className="mb-10 text-sm md:text-base text-muted max-w-2xl" data-testid="chair-category-tagline">{cat.tagline}</p>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                        data-testid="chairs-grid"
                    >
                        {cat.chairs.map((chair) => (
                            <div
                                key={chair.name}
                                className="group border border-line bg-bone hover:border-gold transition-colors duration-300 flex flex-col"
                                data-testid={`chair-card-${chair.name.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div className="relative overflow-hidden bg-white p-6 flex items-center justify-center h-[260px] md:h-[300px]">
                                    <img
                                        src={chair.img}
                                        alt={`${chair.name} office chair - ${chair.model}`}
                                        loading="lazy"
                                        className="max-h-full w-auto object-contain transition-transform duration-500 ease-out group-hover:scale-105"
                                    />
                                    <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.2em] text-muted border border-line bg-white px-2 py-1">
                                        {chair.series}
                                    </span>
                                </div>
                                <div className="p-6 border-t border-line flex-1 flex flex-col">
                                    <div className="flex items-baseline justify-between gap-3">
                                        <h3 className="font-heading font-bold tracking-tight text-navy text-xl">{chair.name}</h3>
                                        <span className="font-mono text-[11px] tracking-[0.15em] text-gold whitespace-nowrap" data-testid={`chair-model-${chair.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                            {chair.model}
                                        </span>
                                    </div>
                                    <ul className="mt-4 space-y-2.5 flex-1" data-testid={`chair-features-${chair.name.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {chair.features.map((f) => (
                                            <li key={f} className="flex items-start gap-2.5 text-[13px] text-charcoal leading-snug">
                                                <Check size={13} className="text-gold mt-0.5 shrink-0" />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="mt-4 pt-4 border-t border-line font-mono text-[10px] tracking-[0.12em] text-muted uppercase leading-relaxed">
                                        {chair.materials}
                                    </p>
                                    <a
                                        href={waFor(chair, cat.label)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-4 inline-flex items-center gap-2 text-[13px] font-medium text-navy hover:text-gold transition-colors"
                                        data-testid={`chair-quote-${chair.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        <MessageCircle size={14} /> Get Bulk Price
                                    </a>
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </AnimatePresence>

                <p className="mt-10 text-sm text-muted" data-testid="chairs-more-note">
                    Need a model not shown here? The full catalog has 40+ models —{" "}
                    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="text-gold font-medium hover:underline" data-testid="chairs-whatsapp-link">
                        WhatsApp us your requirement
                    </a>
                    .
                </p>
            </div>
        </section>
    );
};
